import { useState } from 'react'
import { useI18n } from '@muse/i18n'

import { getCollectionAudiosUrl } from '@core/api'
import { convertAudioPaginated } from '@core/converters'
import { Logger } from '@core/logger'
import type { TCollectionSimple } from '@core/models'
import { useActions, useValues } from '@core/store'
import { toast } from '@ds/components'
import { audioPlayerStore } from '@features/audio-player'
import { getCollectionAudios } from '../services/getCollectionAudios'

type TUseCollectionPlaybackResult = {
    isPlaying: boolean
    isLoading: boolean
    playPause: () => void
}

/**
 * Play a collection from a card: the collection's tracks are not in the payload, so the first play
 * fetches them and seeds the shared playlist. Analytics stay in the caller.
 */
export const useCollectionPlayback = (
    collection: TCollectionSimple,
): TUseCollectionPlaybackResult => {
    const { t } = useI18n()
    const [isLoading, setIsLoading] = useState(false)
    const [playlist, isPlaying] = useValues([
        audioPlayerStore.props.getPlaylist,
        audioPlayerStore.props.getIsPlaying,
    ])
    const { setPlaylist, playAudio, setIsPlaying } = useActions({
        setPlaylist: audioPlayerStore.actions.setPlaylist,
        playAudio: audioPlayerStore.actions.playAudio,
        setIsPlaying: audioPlayerStore.actions.setIsPlaying,
    })

    const isActiveCollection = playlist.id === collection.id

    const playPause = () => {
        if (isActiveCollection) {
            setIsPlaying(!isPlaying)

            return
        }

        setIsLoading(true)

        getCollectionAudios({
            username: collection.authorUsername,
            slug: collection.slug,
            page: 1,
        })
            .then(response => {
                const { items, pagination } = convertAudioPaginated(response)

                if (items.length === 0) {
                    return
                }

                setPlaylist({
                    id: collection.id,
                    collectionId: collection.id,
                    items,
                    total: pagination.total,
                    pages: pagination.pages,
                    page: pagination.page,
                    url: getCollectionAudiosUrl(collection.authorUsername, collection.slug),
                })
                playAudio(0)
            })
            .catch(error => {
                setIsPlaying(false)
                Logger.error('Collection playback error', { error, collectionId: collection.id })
                toast.error(t('i18n.collectionPlaybackError'))
            })
            .finally(() => setIsLoading(false))
    }

    return { isPlaying: isActiveCollection && isPlaying, isLoading, playPause }
}
